import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
  MenuItem,
  Alert,
  Link,
  IconButton
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';

const CreateAsanaTaskModal = ({ open, onClose, item, connectionContext }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [error, setError] = useState(null);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [taskResult, setTaskResult] = useState(null); // To store success result

  // Effect to pre-populate and fetch projects
useEffect(() => {
  if (open) {
    // Reset state for new opening
    setError(null);
    setTaskResult(null);
    setIsCreatingTask(false);
    setSelectedProject('');

    // Pre-populate task details from the item
    if (item) {
      const name = item.dataType === 'question' ? (item.questionText || '') : (item.title || '');
      const notes = item.dataType === 'question' ? `Answer:\n${item.answer || ''}` : (item.content || '');
      setTaskName(name.substring(0, 200)); // Limit name length
      setTaskNotes(notes);
    }

    // Fetch Asana projects
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      try {
        const functions = getFunctions(undefined, 'us-central1');
        const getAsanaProjects = httpsCallable(functions, 'getAsanaProjects');
        
        // NEW: Build payload based on context
        const payload = {};
        if (connectionContext?.type === 'client') {
          payload.clientId = connectionContext.id;
        }

        const result = await getAsanaProjects(payload); // Pass the payload
        if (result.data.success) {
          setProjects(result.data.projects || []);
        } else {
          throw new Error(result.data.message || 'Failed to fetch projects.');
        }
      } catch (err) {
        console.error("Error fetching Asana projects:", err);
        setError(err.message || 'Could not load projects. Please ensure the relevant Asana account is connected.');
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }
}, [open, item, connectionContext]);

  const handleCreateTask = async () => {
  if (!taskName.trim() || !selectedProject) {
    toast.error("Task Name and Project are required.");
    return;
  }

  setIsCreatingTask(true);
  setError(null);
  try {
    const functions = getFunctions(undefined, 'us-central1');
    const createAsanaTask = httpsCallable(functions, 'createAsanaTask');

    // NEW: Build payload with context
    const payload = {
      projectId: selectedProject,
      taskName: taskName.trim(),
      taskNotes: taskNotes.trim()
    };
    if (connectionContext?.type === 'client') {
      payload.clientId = connectionContext.id;
    }
    
    const result = await createAsanaTask(payload); // Pass the payload

    if (result.data.success) {
      toast.success("Task created successfully in Asana!");
      setTaskResult({ url: result.data.taskUrl });
    } else {
      throw new Error(result.data.message || 'Failed to create task');
    }
  } catch (err) {
    console.error('Error creating Asana task:', err);
    setError(err.message || 'An unexpected error occurred.');
  } finally {
    setIsCreatingTask(false);
  }
};

  const handleClose = () => {
    if (isCreatingTask) return;
    onClose();
  };
  
  if (!item) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Create a New Task in Asana
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {taskResult ? (
          <Alert severity="success" sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography>Task created successfully!</Typography>
              <Link href={taskResult.url} target="_blank" rel="noopener noreferrer" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}>
                View in Asana <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
              </Link>
            </Box>
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
  This will create a new task in the relevant Asana account.
</Typography>
            <TextField
              label="Task Name"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Task Notes (Description)"
              value={taskNotes}
              onChange={(e) => setTaskNotes(e.target.value)}
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            {isLoadingProjects ? (
              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <CircularProgress size={20} />
                <Typography sx={{ ml: 2 }}>Loading Asana projects...</Typography>
              </Box>
            ) : (
              <TextField
                select
                label="Select Asana Project"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                fullWidth
                variant="outlined"
                required
                error={!!error}
                helperText={error}
              >
                {projects.length > 0 ? (
                  projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No projects found in your Asana account.</MenuItem>
                )}
              </TextField>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={handleClose}>
          {taskResult ? 'Close' : 'Cancel'}
        </Button>
        {!taskResult && (
          <Button
            variant="contained"
            disabled={isCreatingTask || isLoadingProjects || !taskName.trim() || !selectedProject}
            onClick={handleCreateTask}
            sx={{ position: 'relative' }}
          >
            Create Task
            {isCreatingTask && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAsanaTaskModal;